import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileUp, Loader2, MoreHorizontal, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { createRedeemCodeBatch, exportRedeemCodes, listRedeemCodeBatches, revokeBatchUnused } from '@/api/member';
import type { MemberTypeConfig, RedeemCodeBatch } from '@shared/api.interface';
import { REDEEM_BATCH_MAX_QUANTITY } from '@shared/api.interface';
import { extractApiErrorMessage } from './api-error';
import { ImportOrderNosDialog } from './ImportOrderNosDialog';
import {
  buildExportFile,
  dailyLimitWarning,
  describeMemberType,
  downloadTextFile,
  type ExportFormat,
} from './redeem-batch-file';

interface RedeemBatchPanelProps {
  memberTypeConfigs: MemberTypeConfig[];
  onViewBatch: (batchId: number) => void;
}

// 使用情况条：已用完 / 使用中 / 未激活 / 其他（过期、冻结）/ 已作废
const SEGMENTS: Array<{ key: keyof RedeemCodeBatch; label: string; color: string }> = [
  { key: 'used_up', label: '已用完', color: 'bg-sky-500' },
  { key: 'in_use', label: '使用中', color: 'bg-emerald-500' },
  { key: 'inactive', label: '未激活', color: 'bg-stone-300' },
  { key: 'expired', label: '已过期', color: 'bg-stone-500' },
  { key: 'frozen', label: '冻结', color: 'bg-amber-500' },
  { key: 'revoked', label: '已作废', color: 'bg-red-500' },
];

const formatDate = (value: string) => (value ? new Date(value).toLocaleDateString('zh-CN') : '-');

export function RedeemBatchPanel({ memberTypeConfigs, onViewBatch }: RedeemBatchPanelProps) {
  const [batches, setBatches] = useState<RedeemCodeBatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ member_type: '', quantity: '100', product_label: '', note: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [exportingBatch, setExportingBatch] = useState<{ batch: RedeemCodeBatch; mode: 'unexported' | 'all' } | null>(null);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('txt');
  const [isExporting, setIsExporting] = useState(false);
  const [moreBatch, setMoreBatch] = useState<RedeemCodeBatch | null>(null);
  const [revokeConfirmText, setRevokeConfirmText] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  // 类型筛选只列出已有批次的会员类型
  const batchTypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const batch of batches) counts.set(batch.member_type, (counts.get(batch.member_type) ?? 0) + 1);
    return [...counts.entries()];
  }, [batches]);
  // 刷新后所选类型的批次没了，就回到“全部”
  const activeType = batchTypes.some(([type]) => type === typeFilter) ? typeFilter : '';
  const visibleBatches = activeType ? batches.filter(batch => batch.member_type === activeType) : batches;

  const fetchBatches = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const result = await listRedeemCodeBatches();
      setBatches(result.items);
    } catch {
      setLoadError('获取批次失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if (!form.member_type && memberTypeConfigs.length) {
      const first = memberTypeConfigs[0];
      setForm(prev => ({ ...prev, member_type: first.name, product_label: `小红书-${first.name}` }));
    }
  }, [form.member_type, memberTypeConfigs]);

  const selectedType = useMemo(
    () => memberTypeConfigs.find(config => config.name === form.member_type),
    [form.member_type, memberTypeConfigs],
  );
  const quantity = Number(form.quantity);
  const quantityError = !Number.isInteger(quantity) || quantity < 1 || quantity > REDEEM_BATCH_MAX_QUANTITY
    ? `数量必须是 1–${REDEEM_BATCH_MAX_QUANTITY} 之间的整数`
    : '';
  const warning = dailyLimitWarning(selectedType);

  const handleCreate = async () => {
    if (!form.member_type || quantityError || !form.product_label.trim() || isCreating) return;
    setIsCreating(true);
    try {
      const { batch } = await createRedeemCodeBatch({
        member_type: form.member_type,
        quantity,
        product_label: form.product_label.trim(),
        note: form.note.trim() || undefined,
      });
      toast.success(`已生成 ${batch.quantity} 个兑换码，状态为未激活`);
      setForm(prev => ({ ...prev, note: '' }));
      await fetchBatches();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '生成兑换码失败'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleExport = async () => {
    if (!exportingBatch || isExporting) return;
    setIsExporting(true);
    try {
      const result = await exportRedeemCodes(exportingBatch.batch.id, { mode: exportingBatch.mode });
      if (!result.codes.length) {
        toast.info('没有可以导出的码');
      } else {
        downloadTextFile(buildExportFile(result, exportFormat));
        toast.success(`已导出 ${result.codes.length} 个码`);
      }
      setExportingBatch(null);
      await fetchBatches();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '导出失败'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleRevokeUnused = async () => {
    if (!moreBatch || revokeConfirmText.trim() !== '作废' || isRevoking) return;
    setIsRevoking(true);
    try {
      const result = await revokeBatchUnused(moreBatch.id);
      toast.success(`已作废 ${result.revoked} 个未激活的码`);
      setMoreBatch(null);
      await fetchBatches();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '作废失败'));
    } finally {
      setIsRevoking(false);
    }
  };

  const openMore = (batch: RedeemCodeBatch) => {
    setRevokeConfirmText('');
    setMoreBatch(batch);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        按小红书商品批量生成兑换码，导出后放进发货软件的卡密库。码在用户第一次使用时才开始计算有效期；后台手动创建的会员不受影响。
      </p>

      {/* 生成 */}
      <section aria-labelledby="batch-create-title" className="rounded-2xl bg-white p-5 shadow-[0_0_0_1px_var(--border)]">
        <h2 id="batch-create-title" className="text-lg font-semibold">生成一批兑换码</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1.2fr_0.6fr_1.2fr_1fr]">
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium" htmlFor="batch-type">会员类型</label>
            <Select
              value={form.member_type}
              onValueChange={(value) => setForm(prev => ({ ...prev, member_type: value, product_label: `小红书-${value}` }))}
            >
              <SelectTrigger id="batch-type" className="h-11">
                <SelectValue placeholder="请选择会员类型" />
              </SelectTrigger>
              <SelectContent>
                {memberTypeConfigs.map(config => (
                  <SelectItem key={config.id} value={config.name}>{config.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{describeMemberType(selectedType) || '列表包含你在“会员类型”里新建的所有类型'}</p>
          </div>
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium" htmlFor="batch-quantity">数量</label>
            <Input
              id="batch-quantity"
              inputMode="numeric"
              className="h-11"
              value={form.quantity}
              aria-invalid={Boolean(quantityError)}
              onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value.replace(/\D/g, '') }))}
            />
            <p className={`text-xs ${quantityError ? 'text-destructive' : 'text-muted-foreground'}`}>{quantityError || `单批最多 ${REDEEM_BATCH_MAX_QUANTITY}`}</p>
          </div>
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium" htmlFor="batch-label">商品标签</label>
            <Input
              id="batch-label"
              className="h-11"
              maxLength={100}
              value={form.product_label}
              onChange={(e) => setForm(prev => ({ ...prev, product_label: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">方便对账，例如“小红书-任选三个”；同时记为来源渠道</p>
          </div>
          <div className="min-w-0 space-y-1.5">
            <label className="text-sm font-medium" htmlFor="batch-note">备注（选填）</label>
            <Input
              id="batch-note"
              className="h-11"
              maxLength={500}
              placeholder="例如：国庆补货"
              value={form.note}
              onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
            />
          </div>
        </div>
        {warning && (
          <p role="alert" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{warning}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">码长 10 位，只用不易看错的大写字母和数字（不含 0、O、1、I、L）。</p>
          <Button
            onClick={handleCreate}
            disabled={!form.member_type || Boolean(quantityError) || !form.product_label.trim() || isCreating}
            className="bg-cta text-cta-foreground hover:bg-cta-hover"
          >
            {isCreating && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            生成
          </Button>
        </div>
      </section>

      {/* 批次列表 */}
      <section aria-labelledby="batch-list-title" className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
          <h2 id="batch-list-title" className="text-lg font-semibold">全部批次</h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsImportOpen(true)}>
              <FileUp className="mr-1 h-4 w-4" />
              导入发货记录（补订单号）
            </Button>
            <Button variant="outline" size="sm" onClick={() => void fetchBatches()} disabled={isLoading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </div>
        {batchTypes.length > 1 && (
          <div className="flex flex-wrap gap-2 px-5 pb-4" role="group" aria-label="按会员类型筛选批次">
            {[['', batches.length] as [string, number], ...batchTypes].map(([type, count]) => {
              const active = activeType === type;
              return (
                <button
                  key={type || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTypeFilter(type)}
                  className={`h-8 rounded-full px-3.5 text-sm transition-colors duration-150 ease-out ${active ? 'bg-cta font-medium text-cta-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'}`}
                >
                  {type || '全部'} {count}
                </button>
              );
            })}
          </div>
        )}
        {loadError ? (
          <div role="alert" className="border-t px-5 py-6 text-sm text-destructive">
            {loadError}
            <Button variant="outline" size="sm" className="ml-3" onClick={() => void fetchBatches()}>重试</Button>
          </div>
        ) : isLoading && !batches.length ? (
          <div className="flex justify-center border-t py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !batches.length ? (
          <p className="border-t px-5 py-10 text-center text-sm text-muted-foreground">还没有生成过兑换码。在上面选好会员类型和数量，点“生成”。</p>
        ) : (
          <div className="overflow-x-auto border-t">
            <table className="w-full min-w-[860px] table-auto">
              <thead className="bg-background text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">批次</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">会员类型</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">总数</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">未导出</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">使用情况</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y tabular-nums">
                {visibleBatches.map(batch => (
                  <tr key={batch.id} className="align-top">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-semibold">{batch.product_label}</div>
                      <div className="text-xs text-muted-foreground">#{batch.id} · {formatDate(batch.created_at)} 生成{batch.note ? ` · ${batch.note}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">{batch.member_type}</td>
                    <td className="px-4 py-3 text-right text-sm">{batch.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      {batch.unexported > 0
                        ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">{batch.unexported}</span>
                        : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex h-1.5 w-40 overflow-hidden rounded-full bg-muted" aria-hidden>
                        {SEGMENTS.map(segment => (
                          <span key={segment.key} className={segment.color} style={{ width: `${(Number(batch[segment.key]) / batch.quantity) * 100}%` }} />
                        ))}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {SEGMENTS.filter(segment => segment.key === 'inactive' || segment.key === 'in_use' || Number(batch[segment.key]) > 0).map(segment => (
                          <span key={segment.key} className="inline-flex items-center gap-1">
                            <span className={`h-2 w-2 rounded-sm ${segment.color}`} aria-hidden />
                            {segment.label} {Number(batch[segment.key])}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="bg-cta text-cta-foreground hover:bg-cta-hover"
                          disabled={batch.unexported === 0}
                          onClick={() => setExportingBatch({ batch, mode: 'unexported' })}
                        >
                          导出未导出的 {batch.unexported} 个
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openMore(batch)}>
                          <MoreHorizontal className="mr-1 h-4 w-4" />
                          更多
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 导出 */}
      <Dialog open={!!exportingBatch} onOpenChange={(open) => !open && setExportingBatch(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>导出：{exportingBatch?.batch.product_label}</DialogTitle>
            <DialogDescription>
              {exportingBatch?.mode === 'all'
                ? '重新导出本批次全部可用的码（包括已经导出过的，不含已作废）。'
                : `这次会导出 ${exportingBatch?.batch.unexported ?? 0} 个还没导出过的码，导出后标记为“已导出”，下次不会再导出，避免同一个码发给两个人。已作废的码不会导出。`}
            </DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">文件格式</legend>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="export-format" className="mt-1" checked={exportFormat === 'txt'} onChange={() => setExportFormat('txt')} />
              <span>TXT：一行一个码（大多数发货软件的卡密导入格式）</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="export-format" className="mt-1" checked={exportFormat === 'csv'} onChange={() => setExportFormat('csv')} />
              <span>CSV：兑换码、会员类型、批次（可以用 Excel 打开）</span>
            </label>
          </fieldset>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportingBatch(null)}>取消</Button>
            <Button onClick={handleExport} disabled={isExporting} className="bg-cta text-cta-foreground hover:bg-cta-hover">
              {isExporting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              导出并标记
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 更多操作 */}
      <Dialog open={!!moreBatch} onOpenChange={(open) => !open && setMoreBatch(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>批次 #{moreBatch?.id}：{moreBatch?.product_label}</DialogTitle>
            <DialogDescription>{moreBatch?.member_type} · 共 {moreBatch?.quantity} 个，其中 {moreBatch?.inactive} 个还没人用过</DialogDescription>
          </DialogHeader>
          {moreBatch && (
            <div className="space-y-4">
              <ul className="divide-y rounded-xl border text-sm">
                <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                  <span>查看本批次所有码</span>
                  <Button size="sm" variant="outline" onClick={() => { setMoreBatch(null); onViewBatch(moreBatch.id); }}>去会员管理</Button>
                </li>
                <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                  <span>重新导出全部（包括已经导出过的）</span>
                  <Button size="sm" variant="outline" onClick={() => { setMoreBatch(null); setExportingBatch({ batch: moreBatch, mode: 'all' }); }}>重新导出</Button>
                </li>
              </ul>
              <div className="space-y-3 rounded-xl bg-red-50 p-3 text-sm text-red-800">
                <p>
                  <strong className="font-semibold">作废本批次所有未激活的码</strong>
                  <br />
                  码整批泄露时使用。会作废 {moreBatch.inactive} 个还没人用过的码，已经在用的码不受影响。作废后不能恢复。
                </p>
                <div className="space-y-1.5">
                  <label htmlFor="batch-revoke-confirm" className="font-medium">输入“作废”两个字确认</label>
                  <Input
                    id="batch-revoke-confirm"
                    autoComplete="off"
                    className="bg-white"
                    value={revokeConfirmText}
                    onChange={(e) => setRevokeConfirmText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoreBatch(null)}>关闭</Button>
            <Button
              variant="destructive"
              disabled={!moreBatch || moreBatch.inactive === 0 || revokeConfirmText.trim() !== '作废' || isRevoking}
              onClick={handleRevokeUnused}
            >
              {isRevoking && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              作废 {moreBatch?.inactive ?? 0} 个未激活码
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportOrderNosDialog open={isImportOpen} onClose={() => setIsImportOpen(false)} />
    </div>
  );
}
